<template>
  <section class="chapter">
    <ErrorMessage v-if="chapterError">
      <p>{{ chapterError }}</p>
      <p>
        <ThreeDButton icon="redo" @click="fetchChapterData">
          {{ $t('actions.retry') }}
        </ThreeDButton>
      </p>
    </ErrorMessage>
    <LoadingOverlay v-else-if="!chapterData" />
    <template v-else>
      <div
        class="chapter-header"
        :class="{ 'hide-chapter-nav': hideChapterNavigation }"
      >
        <div class="chapter-title">
          <p>
            <span class="chapter-index"
              >{{ $t('book-shelf.chapter') }} {{ chapterIndex }}:</span
            >
            {{ chapterData.name }} {{ currentNavigationIndex + 1 }}/{{
              chapterData.lessons.length + 1
            }}
          </p>
        </div>

        <RadioButtonNavigation
          v-if="displaySiteNavigation"
          class="site-navigation"
          :links="siteNavigationLinks"
        />

        <ChapterNavigation
          ref="chapterNavigation"
          :book-id="bookId"
          :chapter-id="chapterId"
          :current-section-id="sectionId"
          :current-section-type="sectionType"
          :class="{ 'hide-chapter-nav': hideChapterNavigation }"
        />

        <div
          class="chapter-navigation-footer"
          @click="toggleOpenCloseNavbar"
          role="button"
        >
          <FaIcon
            icon="angle-up"
            class="chapter-navigation-footer-icon"
            :class="{ rotate: hideChapterNavigation }"
          />
        </div>
      </div>
      <div class="chapter-content-wrapper">
        <div class="chapter-content">
          <RouterView
            :chapter-data="chapterData"
            :chapter-index="chapterIndex"
          />
        </div>
        <div class="navigation-button-wrapper">
          <ThreeDButton
            v-if="previousPageRouteParams"
            :to="previousPageRouteParams"
            class="previous-page-button"
            icon="arrow-left"
          >
            {{ $t('book-shelf.previous') }}
          </ThreeDButton>
          <ThreeDButton
            v-if="nextPageRouteParams"
            :to="nextPageRouteParams"
            class="next-page-button"
            icon="arrow-right"
            icon-position="right"
          >
            {{ $t('book-shelf.next') }}
          </ThreeDButton>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import ChapterNavigation from '@/components/books/ChapterNavigation'
import ErrorMessage from '@/components/ui/ErrorMessage'
import FaIcon from '@/components/ui/FaIcon'
import LoadingOverlay from '@/components/LoadingOverlay'
import RadioButtonNavigation from '@/components/ui/RadioButtonNavigation'
import ThreeDButton from '@/components/ThreeDButton'

import { AuthStoreNamespacedTypes } from '@/store/auth'
import { BookStoreNamespacedTypes } from '@/store/books'

import { createSlug, getIdFromSlug } from '@/utils/string'

import { ChapterSectionType, UserType } from '@/enums'

import config from '../../../config.json'

export default {
  name: 'Chapter',
  components: {
    ChapterNavigation,
    ErrorMessage,
    FaIcon,
    LoadingOverlay,
    RadioButtonNavigation,
    ThreeDButton,
  },
  props: {
    bookSlug: {
      type: String,
      required: true,
    },
    chapterSlug: {
      type: String,
      required: true,
    },
    sectionSlug: {
      type: String,
      required: false,
    },
    sectionTypeString: {
      type: String,
      required: false,
      default: ChapterSectionType.INTRO.toString(),
    },
  },
  data() {
    return {
      ChapterSectionType,
      hideChapterNavigation: false,
    }
  },
  computed: {
    /**
     * @return {number}
     */
    bookId() {
      return getIdFromSlug(this.bookSlug)
    },

    /**
     * @return {number}
     */
    chapterId() {
      return getIdFromSlug(this.chapterSlug)
    },

    /**
     * @return {number}
     */
    currentNavigationIndex() {
      return this.$store.getters[
        BookStoreNamespacedTypes.getters.GET_CHAPTER_NAVIGATION_ITEM_INDEX
      ]({
        bookId: this.bookId,
        chapterId: this.chapterId,
        sectionType: this.sectionType,
        sectionId: this.sectionId,
      })
    },

    /**
     * @return {number}
     */
    sectionId() {
      return getIdFromSlug(this.sectionSlug || '')
    },

    /**
     * @return {number}
     */
    sectionType() {
      return parseInt(this.sectionTypeString)
    },

    navigationItems() {
      return this.$store.getters[
        BookStoreNamespacedTypes.getters.GET_CHAPTER_NAVIGATION_ITEMS
      ]({ bookId: this.bookId, chapterId: this.chapterId })
    },

    /**
     * @return {Object}
     */
    chapterData() {
      return this.$store.getters[BookStoreNamespacedTypes.getters.GET_CHAPTER](
        this.chapterId
      )
    },

    /**
     * @return {number}
     */
    chapterIndex() {
      return this.$store.getters[
        BookStoreNamespacedTypes.getters.GET_CHAPTER_INDEX
      ]({ bookId: this.bookId, chapterId: this.chapterId })
    },

    /**
     * @return {String|undefined}
     */
    chapterError() {
      return this.$store.getters[
        BookStoreNamespacedTypes.getters.GET_CHAPTER_ERROR
      ](this.chapterId)
    },

    /**
     * @return {Array}
     */
    chapters() {
      return this.$store.getters[BookStoreNamespacedTypes.getters.GET_CHAPTERS](
        this.bookId
      )
    },

    /**
     * @returns {boolean}
     */
    displaySiteNavigation() {
      return (
        this.$store.getters[
          AuthStoreNamespacedTypes.getters.GET_USER_TYPE
        ]() !== UserType.STUDENT
      )
    },

    /**
     * The links and names to use in the site navigation
     * @returns {Array<{name: String, url: String}>}
     */
    siteNavigationLinks() {
      return [
        {
          name: this.$t('labels.student'),
          url: `${config.studentSiteUrl}/books${this.$route.path}`,
        },
        {
          name: this.$t('labels.teacher'),
          url: `${config.teacherSiteUrl}/books${this.$route.path}`,
        },
      ]
    },

    nextPageRouteParams() {
      return this.getNavigationRouteParamsByIndex(
        this.currentNavigationIndex + 1
      )
    },

    previousPageRouteParams() {
      return this.getNavigationRouteParamsByIndex(
        this.currentNavigationIndex - 1
      )
    },
  },
  watch: {
    /**
     * Fetches new chapter data and status when the chapter ID (the route) changes.
     */
    chapterId: {
      handler: function() {
        this.fetchChapterData()
      },
      immediate: true,
    },
  },
  methods: {
    createSlug,

    fetchChapterData() {
      this.$store.dispatch(
        BookStoreNamespacedTypes.actions.FETCH_CHAPTER,
        this.chapterId
      )
      this.$store.dispatch(
        BookStoreNamespacedTypes.actions.FETCH_CHAPTER_STATUS,
        this.chapterId
      )
    },

    toggleOpenCloseNavbar() {
      this.hideChapterNavigation = !this.hideChapterNavigation
    },

    /**
     * Returns the routing parameters for the navigation item at the given index.
     *
     * @param {number}index
     * @return {{name:  string, params: Object}|null}}
     */
    getNavigationRouteParamsByIndex(index) {
      const navigationItem = this.navigationItems[index]

      if (!navigationItem) {
        return null
      }

      let params = {}

      switch (navigationItem.sectionType) {
        case ChapterSectionType.INTRO:
          params = { name: 'book-chapter' }
          break
        case ChapterSectionType.LESSON:
        case ChapterSectionType.TRIAL_EXAM:
          params = {
            name: 'book-chapter-section',
            params: {
              sectionTypeString: navigationItem.sectionType.toString(),
              sectionSlug: createSlug(navigationItem.title, navigationItem.id),
            },
          }
      }

      return params
    },
  },
}
</script>

<style lang="scss" scoped>
.chapter {
  flex-basis: 0;
  flex-grow: 1;
}

.chapter-header {
  position: sticky;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-start;
  background: $white;
  box-shadow: $mainBoxShadow;
  z-index: 4;

  // Compensate for the main navigation menu and .book-title-wrapper
  top: calc(var(--menu-height-compensation) + 3.1875rem);

  &.hide-chapter-nav {
    background-color: transparent;
    box-shadow: none;
  }
}

.chapter-title {
  border-radius: 0 0 1.5em 0;
  padding: 0.5em 2em;
  width: auto;
  left: 0;
  background: $blueSemiDark;
  color: $white;

  .chapter-index {
    font-weight: 600;
    text-transform: uppercase;
  }

  @media (max-width: $minWidthSmallTablet) {
    padding-left: $chapterNavigationWidth;
  }
}

.chapter-navigation {
  &.hide-chapter-nav {
    display: none !important;
  }
}

.chapter-content-wrapper {
  flex: 1 1 auto;
  position: relative;
  padding: var(--content-holder-margin);
}

.navigation-button-wrapper {
  display: grid;
  grid-template-columns: [left] 50% [right] 50%;
  padding: 2em 0;
  font-size: $copyFontSize;

  .previous-page-button {
    grid-column-start: left;
    justify-self: start;
  }
  .next-page-button {
    grid-column-start: right;
    justify-self: end;
  }
}

.site-navigation {
  position: absolute;
  right: 1em;
  top: 0.5em;
}

.chapter-navigation-footer {
  display: flex;
  position: absolute;
  justify-content: center;
  font-size: 1.2em;
  cursor: pointer;
  bottom: 0.75em;
  left: calc(50% - 0.6875rem);
  padding: 0.3em 0.25em 0.2em;
  width: 1.375rem;
  background-color: #fff;
  border-radius: 1.375rem;
  box-shadow: $mainBoxShadow;

  .chapter-navigation-footer-icon {
    font-size: 1.5em;
    transform: rotate(0deg);
    transition-duration: $transitionMedium;

    &.rotate {
      transform: rotate(180deg);
    }
  }
}

@media (max-width: 30rem) {
  .site-navigation {
    position: static;
    margin: 1.5em auto -0.5em;
  }
}

@media (max-height: 50rem) {
  .chapter-header {
    position: relative;
    top: 0;
    z-index: 0;
  }
}

@media (max-width: $minWidthSmallTablet) {
  .chapter-header.hide-chapter-nav .site-navigation {
    display: none;
  }
  .chapter-navigation-footer {
    left: auto;
    right: 0.75em;
  }

  .chapter-content-wrapper {
    padding: 1em 0;
  }

  .navigation-button-wrapper {
    padding: 2em var(--content-holder-margin);
  }
}
</style>
